import React, { useState } from "react";
import { Heart, Server, Wrench, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PRESETS = [3, 5, 10, 20];

export default function SupportMtaa() {
  const [amount, setAmount] = useState(5);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState(false);

  const raw = custom.trim();
  const parsed = raw === "" ? amount : Number(raw);
  const valid = Number.isFinite(parsed) && parsed >= 1 && parsed <= 1000;
  const effective = valid ? parsed : null;

  const contribute = async () => {
    if (!valid) return toast.error("Enter an amount between A$1 and A$1000");
    setBusy(true);
    setCheckoutError(false);
    try {
      const { data } = await api.post("/payments/support/checkout", {
        amount: Math.round(effective * 100),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { setCheckoutError(true); setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6" data-testid="support-mtaa-page">
      <div className="space-y-2">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Support Mtaa ❤️</h1>
        <p className="text-lg font-semibold text-brand-green">Help us grow Mtaa</p>
        <p className="text-sm text-muted-foreground">
          Mtaa is being built to connect our community across Australia. If you're enjoying the
          platform and want to support its continued development, you can make an optional contribution.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="support-info-cards">
        {[["Hosting", Server, "Keeping Mtaa online and reliable."],
          ["Maintenance", Wrench, "Security, fixes and ongoing improvements."],
          ["New features", Sparkles, "Building useful features for the community."]].map(([label, Icon, desc]) => (
          <div key={label} className="flex cursor-default items-start gap-3 rounded-2xl border border-border bg-card/60 p-4">
            <Icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-green" />
            <div>
              <p className="text-sm font-semibold text-foreground">{label}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Your support helps with hosting, development, maintenance and building new features for the Mtaa community.
      </p>

      <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <p className="text-sm font-medium">Choose an amount (AUD)</p>
        <div className="grid grid-cols-4 gap-2">
          {PRESETS.map((p) => (
            <button key={p} data-testid={`support-amount-${p}`}
              onClick={() => { setAmount(p); setCustom(""); }}
              className={`rounded-xl border py-3 text-sm font-bold transition-colors ${custom === "" && amount === p ? "border-brand-green bg-brand-green/10 text-brand-green" : "border-border hover:border-brand-green/40"}`}>
              A${p}
            </button>
          ))}
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Or enter a custom amount</label>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-border px-3">
            <span className="text-muted-foreground">A$</span>
            <Input type="number" min="1" max="1000" step="0.01" inputMode="decimal" value={custom} placeholder="Custom amount"
              onChange={(e) => setCustom(e.target.value)} data-testid="support-custom-amount"
              className="border-0 px-0 shadow-none focus-visible:ring-0" />
          </div>
          {raw !== "" && !valid && <p className="mt-1 text-xs text-destructive" data-testid="support-amount-error">Enter an amount between A$1 and A$1000.</p>}
        </div>
        {checkoutError && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-center" data-testid="support-checkout-error">
            <p className="text-sm text-destructive">Payment couldn't be started. Please try again.</p>
            <Button variant="outline" className="mt-2" onClick={contribute} disabled={busy || !valid} data-testid="support-retry-btn">Try Again</Button>
          </div>
        )}
        <Button className="w-full" onClick={contribute} disabled={busy || !valid} data-testid="support-contribute-btn">
          <Heart className="h-4 w-4" />{busy ? "Starting payment…" : "Continue to payment"}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          Completely optional. Mtaa's core community features remain available whether you contribute or not.
          Payments are handled securely by Stripe (Apple Pay, Google Pay & card) — Mtaa never sees or stores your card details.
        </p>
      </div>
    </div>
  );
}
